/**
 * Coherent-IA Task 3: the online-tanpura widget that hydrates
 * `[data-tanpura-widget]` on the static `/tanpura/` page — a Sa (pitch
 * class + octave) selector, a big Play/Stop button, and an optional volume
 * slider. Backed by the SAME sample-based `TanpuraSynth`
 * (`audio/tanpura.ts`) the in-app drone button uses (see
 * `src/ui/app.ts`'s `startTanpura`/`stopTanpura`) — no duplicated audio
 * code. It plays the real recorded tanpura for the chosen Sa's nearest
 * pitch class, pitch-shifted (via playback rate) to the exact requested Sa
 * — see `audio/tanpura-samples.ts`'s `resolveTanpuraSample`. This is what
 * makes "online tanpura" on this page an ACTUAL tanpura rather than just a
 * link to one.
 *
 * DOM-only, no framework: one `TanpuraSynth` instance per hydrated widget
 * (normally exactly one per page). Audio only ever starts from the Play
 * button's own click (a user gesture, satisfying autoplay-policy
 * restrictions); any failure inside `tanpura.start()` (a blocked
 * `AudioContext`, a sample fetch/decode error with the fallback synth also
 * failing, etc.) is caught here and surfaced as a quiet, polite status
 * message — this module never throws out of a hydrated widget.
 */
import { noteToFrequency } from '@swarasense/shared';
import { TanpuraSynth } from '../audio/tanpura.js';
import { mountSaControl } from './sa-control.js';

const DEFAULT_VOLUME = 0.2;

function setPlayingUi(button: HTMLButtonElement, label: HTMLElement, playing: boolean): void {
  button.classList.toggle('on', playing);
  button.setAttribute('aria-pressed', String(playing));
  label.textContent = playing ? 'Stop' : 'Play';
}

/**
 * Hydrates one `[data-tanpura-widget]` container. Mounts the shared Sa control
 * (`sa-control.ts`, bound to `profile.sa`) into `[data-sa-control]` for the
 * current Sa, reads `.tanpura-play-btn`/`.tanpura-play-label` for the Play/Stop
 * toggle, and the optional `.tanpura-volume-range` + `.tanpura-widget-status`
 * (aria-live) elements. Silently does nothing if the required controls aren't
 * present — a malformed/future markup change should never throw out of the
 * hydration loop in `content-widgets.ts`.
 *
 * Connected-Swarasense (W1/W2): the tanpura page now reads/writes the SHARED
 * shruti, so a Sa set here follows you to the raga pages and the practice tool
 * (and vice versa) — was its own isolated pitch/octave selects.
 */
export function hydrateTanpuraWidget(container: HTMLElement): void {
  const saSlotEl = container.querySelector<HTMLElement>('[data-sa-control]');
  const playButtonEl = container.querySelector<HTMLButtonElement>('.tanpura-play-btn');
  const playLabelEl = container.querySelector<HTMLElement>('.tanpura-play-label');
  if (!saSlotEl || !playButtonEl || !playLabelEl) return;
  // Re-bound to non-null-typed locals: TS's control-flow narrowing from the
  // guard above doesn't carry into the nested `function` declarations below
  // (their hoisting makes that unsound in general, even for `const`
  // bindings) — these are what those closures actually capture.
  const playButton: HTMLButtonElement = playButtonEl;
  const playLabel: HTMLElement = playLabelEl;

  const volumeInput = container.querySelector<HTMLInputElement>('.tanpura-volume-range');
  const statusEl = container.querySelector<HTMLElement>('.tanpura-widget-status');

  const tanpura = new TanpuraSynth();
  // The shared Sa control — changing Sa restarts a running drone (below).
  const saControl = mountSaControl(saSlotEl, { onChange: () => void restartIfRunning() });
  // Synchronous guard against a fast double-click on the button itself —
  // `TanpuraSynth.start` already guards its own re-entrancy internally, but
  // this keeps the UI (status text, disabled-looking state) from flashing
  // between two overlapping start attempts.
  let starting = false;

  function currentSaHz(): number {
    const sa = saControl.getSa();
    return noteToFrequency(`${sa.pitchClass}${sa.octave}`);
  }

  function currentVolume(): number {
    if (!volumeInput) return DEFAULT_VOLUME;
    const v = Number(volumeInput.value);
    return Number.isFinite(v) ? v : DEFAULT_VOLUME;
  }

  function setStatus(message: string): void {
    if (statusEl) statusEl.textContent = message;
  }

  async function start(): Promise<void> {
    if (tanpura.running || starting) return;
    starting = true;
    setStatus('Starting…');
    try {
      await tanpura.start(currentSaHz(), { volume: currentVolume() });
      setPlayingUi(playButton, playLabel, true);
      setStatus('');
    } catch (err) {
      // Graceful if blocked: TanpuraSynth already falls back from the
      // sample to the synthesized drone internally, so a rejection here
      // means BOTH failed (e.g. no AudioContext support at all, or the
      // gesture requirement wasn't satisfied) — tell the visitor plainly
      // rather than leaving the button looking stuck.
      console.warn('tanpura-widget: could not start the drone', err);
      setStatus("Couldn't start audio — tap Play to try again.");
    } finally {
      starting = false;
    }
  }

  async function stop(): Promise<void> {
    try {
      await tanpura.stop();
    } catch (err) {
      console.warn('tanpura-widget: error stopping the drone', err);
    } finally {
      setPlayingUi(playButton, playLabel, false);
      setStatus('');
    }
  }

  playButton.addEventListener('click', () => {
    void (tanpura.running ? stop() : start());
  });

  /** Changing Sa while the drone is already running restarts it at the new
   * pitch — mirrors `app.ts`'s own store-subscription behaviour for the
   * in-app drone button, so a visitor doesn't have to manually stop/start
   * to hear a pitch-class or octave change take effect. */
  async function restartIfRunning(): Promise<void> {
    if (!tanpura.running) return;
    try {
      await tanpura.stop();
      await tanpura.start(currentSaHz(), { volume: currentVolume() });
    } catch (err) {
      console.warn('tanpura-widget: could not restart the drone at the new Sa', err);
      setPlayingUi(playButton, playLabel, false);
      setStatus("Couldn't restart audio — tap Play to try again.");
    }
  }

  // Sa changes are handled by the shared control's `onChange` (wired at mount
  // above) → `restartIfRunning`.
  volumeInput?.addEventListener('input', () => {
    if (tanpura.running) tanpura.setVolume(currentVolume());
  });
}
